import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "src/dm/utils/axios";
import { APIS } from "src/dm/utils/constants";

// Thin TypeScript hooks for the legacy NewDeal/DealHeader/EditReward/
// ViewItem WebMethods. Each one matches a single endpoint on
// LookupsController.cs.
const baseUrl = "/dm/V1/lookups";

// ---- 1. AD_FEE ---------------------------------------------------------
interface AdFeeResponse {
  catMgrCode: number;
  banner: string | null;
  adFee: number | null;
  adminFee: number | null;
}

export const useAdFee = (
  catMgrCode: number | null | undefined,
  banner?: string
) =>
  useQuery({
    queryKey: ["lookup-ad-fee", catMgrCode, banner],
    enabled: catMgrCode != null,
    queryFn: async (): Promise<AdFeeResponse> => {
      const response = await apiClient.get<AdFeeResponse>(`${baseUrl}/ad-fee`, {
        params: { catMgrCode, banner: banner ?? undefined },
      });
      return response.data;
    },
  });

// ---- 2. AP_VENDORS by VendorBroker ------------------------------------
export interface ApVendorRow {
  apVendorNumber: string;
  apVendorName: string;
}
interface ApVendorsResponse {
  vendorBrokerId: number;
  items: ApVendorRow[];
}

export const useApVendors = (vendorBrokerId: number | null | undefined) =>
  useQuery({
    queryKey: ["lookup-ap-vendors", vendorBrokerId],
    enabled: vendorBrokerId != null,
    queryFn: async (): Promise<ApVendorRow[]> => {
      const response = await apiClient.get<ApVendorsResponse>(
        `${baseUrl}/ap-vendors`,
        { params: { vendorBrokerId } }
      );
      return response.data.items ?? [];
    },
  });

// ---- 3. CAT_MGRS -------------------------------------------------------
export interface CatMgrRow {
  code: string;
  name: string;
}
interface CatMgrsResponse {
  items: CatMgrRow[];
}

export const useCatMgrs = () =>
  useQuery({
    queryKey: ["lookup-cat-mgrs"],
    queryFn: async (): Promise<CatMgrRow[]> => {
      const response = await apiClient.get<CatMgrsResponse>(
        `${baseUrl}/cat-mgrs`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

// ---- 4 + 5. PROMO_CODES ------------------------------------------------
export interface PromoCodeRow {
  code: string;
  label: string;
}
interface PromoCodesResponse {
  items: PromoCodeRow[];
}

export const usePromoCodes = (banner?: string, geac?: boolean) =>
  useQuery({
    queryKey: ["lookup-promo-codes", banner, geac],
    queryFn: async (): Promise<PromoCodeRow[]> => {
      const response = await apiClient.get<PromoCodesResponse>(
        `${baseUrl}/promo-codes`,
        { params: { banner, geac: geac ? "Y" : undefined } }
      );
      return response.data.items ?? [];
    },
  });

export const usePromoCodesForReward = () =>
  useQuery({
    queryKey: ["lookup-promo-codes-for-reward"],
    queryFn: async (): Promise<PromoCodeRow[]> => {
      const response = await apiClient.get<PromoCodesResponse>(
        `${baseUrl}/promo-codes-for-reward`
      );
      return response.data.items ?? [];
    },
  });

// ---- 6. AD_FEATURE_SEQUENCE -------------------------------------------
interface AdFeatureSequenceResponse {
  adStartDate: string;
  adFeatureId: number;
  dealId: number | null;
  sequence: number | null;
}

export const useAdFeatureSequence = (params: {
  adStartDate?: string;
  adFeatureId?: number | null;
  dealId?: number | null;
  enabled?: boolean;
}) =>
  useQuery({
    queryKey: [
      "lookup-ad-feature-sequence",
      params.adStartDate,
      params.adFeatureId,
      params.dealId,
    ],
    enabled:
      (params.enabled ?? true) &&
      !!params.adStartDate &&
      params.adFeatureId != null,
    queryFn: async (): Promise<number | null> => {
      const response = await apiClient.get<AdFeatureSequenceResponse>(
        `${baseUrl}/ad-feature-sequence`,
        {
          params: {
            adStartDate: params.adStartDate,
            adFeatureId: params.adFeatureId,
            dealId: params.dealId ?? undefined,
          },
        }
      );
      return response.data.sequence;
    },
  });

// ---- 7. DEAL_OWNER -----------------------------------------------------
export interface DealOwnerInfo {
  userId: string;
  fullName: string;
}

export const useDealOwner = (userId: string | undefined) =>
  useQuery({
    queryKey: ["lookup-deal-owner", userId],
    enabled: !!userId,
    queryFn: async (): Promise<DealOwnerInfo | null> => {
      try {
        const response = await apiClient.get<DealOwnerInfo>(
          `${baseUrl}/deal-owner`,
          { params: { userId } }
        );
        return response.data;
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 404) return null;
        throw err;
      }
    },
  });

// ---- 8. PROMO_INFO ----------------------------------------------------
export interface PromoInfo {
  promoCode: number;
  description: string | null;
  isBogo: boolean;
  isRewardAmountOff: boolean;
  isRewardPromo: boolean;
  isPercentAmountOff: boolean;
  rewardType: string;
}

export const usePromoInfo = (promoCode: number | undefined) =>
  useQuery({
    queryKey: ["lookup-promo-info", promoCode],
    enabled: promoCode != null,
    queryFn: async (): Promise<PromoInfo> => {
      const response = await apiClient.get<PromoInfo>(
        `${baseUrl}/promo-info`,
        { params: { promoCode } }
      );
      return response.data;
    },
  });

// ---- 9. CHECK_MKT_MSG -------------------------------------------------
export interface CheckMktMsgResponse {
  marketingMessageCode: string;
  result: number;
  requiresProductDescription: boolean;
  requiresTagQuantity: boolean;
}

export const useCheckMarketingMessage = () =>
  useMutation({
    mutationFn: async (
      marketingMessageCode: string
    ): Promise<CheckMktMsgResponse> => {
      const response = await apiClient.get<CheckMktMsgResponse>(
        `${baseUrl}/check-mkt-msg`,
        { params: { marketingMessageCode } }
      );
      return response.data;
    },
  });

// ---- 10. REWARD_TYPE --------------------------------------------------
interface RewardTypeResponse {
  adPlanCode: string | null;
  rewardType: string;
  note: string;
}

export const useRewardType = (adPlanCode: string | undefined) =>
  useQuery({
    queryKey: ["lookup-reward-type", adPlanCode],
    enabled: !!adPlanCode,
    queryFn: async (): Promise<string> => {
      const response = await apiClient.get<RewardTypeResponse>(
        `${baseUrl}/reward-type`,
        { params: { adPlanCode } }
      );
      return response.data.rewardType;
    },
  });

// ---- 11. MARKETING_MESSAGES (static, cached) --------------------------
export interface MarketingMessageRow {
  code: string;
  description: string;
  requiresProductDescription: boolean;
  requiresTagQuantity: boolean;
}
interface MarketingMessagesResponse {
  items: MarketingMessageRow[];
}

export const useMarketingMessages = () =>
  useQuery({
    queryKey: ["lookup-marketing-messages"],
    queryFn: async (): Promise<MarketingMessageRow[]> => {
      const response = await apiClient.get<MarketingMessagesResponse>(
        `${baseUrl}/marketing-messages`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

// ---- 12. BICEPS_POPUP -------------------------------------------------
export interface BicepsPopup {
  dealId: number;
  dealItemId: number;
  upc: number | null;
  description: string;
  bicepsValidated: boolean;
}

export const useBicepsPopup = (
  dealId: number | undefined,
  dealItemId: number | undefined
) =>
  useQuery({
    queryKey: ["lookup-biceps-popup", dealId, dealItemId],
    enabled: dealId != null && dealItemId != null,
    queryFn: async (): Promise<BicepsPopup> => {
      const response = await apiClient.get<BicepsPopup>(
        `${baseUrl}/biceps-popup`,
        { params: { dealId, dealItemId } }
      );
      return response.data;
    },
  });

// ---- 12a. Promo-details dropdowns (audit 21 MAJOR #4) -----------------
// Five small lookup tables the NewDeal/EditDeal form needs to render its
// Promo Details section. All share the shape `{ items: [{ id, label }] }` —
// see LookupsController endpoints 14a-14e. Cached 5 minutes server-side and
// 5 minutes client-side so these are cheap to call repeatedly.

// AD_SIZE has a string id (e.g. "TPR", "UAD"); the rest are numeric ids.
export interface StringLookupRow {
  id: string;
  label: string;
}
export interface NumericLookupRow {
  id: number;
  label: string;
}
interface StringLookupResponse {
  items: StringLookupRow[];
}
interface NumericLookupResponse {
  items: NumericLookupRow[];
}

export const useAdSizes = () =>
  useQuery({
    queryKey: ["lookup-ad-sizes"],
    queryFn: async (): Promise<StringLookupRow[]> => {
      const response = await apiClient.get<StringLookupResponse>(
        `${baseUrl}/ad-sizes`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useAdLocations = () =>
  useQuery({
    queryKey: ["lookup-ad-locations"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/ad-locations`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useDistributionTypes = () =>
  useQuery({
    queryKey: ["lookup-distribution-types"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/distribution-types`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useDisplayPlans = () =>
  useQuery({
    queryKey: ["lookup-display-plans"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/display-plans`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export const useFeatures = () =>
  useQuery({
    queryKey: ["lookup-features"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/features`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

// ---- 13. NOTIFY_EMAIL inline save -------------------------------------
interface NotifyEmailResponse {
  dealId: number;
  email: string | null;
}

export const useSaveNotifyEmail = (dealId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (email: string | null): Promise<NotifyEmailResponse> => {
      const response = await apiClient.patch<NotifyEmailResponse>(
        `${APIS.Deals}/${dealId}/notify-email`,
        { email }
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
    },
  });
};

// ---- 15-20. Add-Item cascading dropdown lookups ------------------------
// One hook per legacy SearchItem.aspx dropdown (ddlFamily / ddlGroup /
// ddlSubGroup / ddlPriceGroup / ddlItemStatus / ddlVendor). All hit
// LookupsController.cs and all share the { items, warning? } envelope.
//
// Scoping rule: every cascading lookup is scoped to the deal's
// CategoryManagerCode + BannerCode so it returns "items this deal could
// pick" rather than the entire catalog. Mirrors legacy
// AvailableItems.GetByFilter on SearchItem.aspx.cs:1131.

export interface CodeLabelRow {
  code: string;
  label: string;
}
interface CodeLabelEnvelope {
  items: CodeLabelRow[];
  /**
   * Server-supplied warning when the underlying lookup table is unreachable
   * (eg the RP_ITEM_AUTH_BANNERS_MV view isn't granted to this DB user).
   * The SPA surfaces this verbatim near the affected field.
   */
  warning?: string | null;
}

interface CascadingScope {
  catMgrCode?: number | null;
  banner?: string | null;
}

/**
 * Cascading-scope queries fire when EITHER the scope is fully populated
 * (deal-bound forms like AddItem) OR the scope is fully empty (cross-deal
 * pages like AdvancedSearch — the API endpoints return the global set when
 * both params are missing). A partially-populated scope is treated as
 * mid-typing and the query stays disabled to avoid spurious bad-cardinality
 * requests.
 */
const cascadingEnabled = (scope: CascadingScope): boolean => {
  const hasCm = scope.catMgrCode != null;
  const hasBanner = !!scope.banner;
  if (hasCm && hasBanner) return true; // deal-bound
  if (!hasCm && !hasBanner) return true; // global
  return false;
};

/**
 * Family dropdown (ddlFamily). Optionally takes a server-side `search`
 * for typeahead — the family list can have ~thousands of rows.
 */
export const useFamiliesLookup = (
  scope: CascadingScope,
  search?: string
) =>
  useQuery({
    queryKey: ["lookup-families", scope.catMgrCode, scope.banner, search ?? ""],
    enabled: cascadingEnabled(scope),
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/families`, {
        params: {
          catMgrCode: scope.catMgrCode ?? undefined,
          banner: scope.banner ?? undefined,
          search: search || undefined,
          take: 200,
        },
      });
      return response.data;
    },
  });

export const useGroupsLookup = (scope: CascadingScope) =>
  useQuery({
    queryKey: ["lookup-groups", scope.catMgrCode, scope.banner],
    enabled: cascadingEnabled(scope),
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/groups`, {
        params: {
          catMgrCode: scope.catMgrCode ?? undefined,
          banner: scope.banner ?? undefined,
        },
      });
      return response.data;
    },
  });

/**
 * Sub-group dropdown — when `groupCode` is supplied, narrows to only the
 * subgroups of that group. Otherwise returns everything in the cat-mgr /
 * banner scope.
 */
export const useSubgroupsLookup = (
  scope: CascadingScope,
  groupCode?: number | null
) =>
  useQuery({
    queryKey: ["lookup-subgroups", scope.catMgrCode, scope.banner, groupCode ?? null],
    enabled: cascadingEnabled(scope),
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/subgroups`, {
        params: {
          catMgrCode: scope.catMgrCode ?? undefined,
          banner: scope.banner ?? undefined,
          groupCode: groupCode ?? undefined,
        },
      });
      return response.data;
    },
  });

export const usePriceGroupsLookup = (scope: CascadingScope) =>
  useQuery({
    queryKey: ["lookup-price-groups", scope.catMgrCode, scope.banner],
    enabled: cascadingEnabled(scope),
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/price-groups`, {
        params: {
          catMgrCode: scope.catMgrCode ?? undefined,
          banner: scope.banner ?? undefined,
        },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Item Status — global, very small lookup (A/D/I). The API returns a fixed
 * list so we cache it indefinitely client-side too.
 */
export const useItemStatusesLookup = () =>
  useQuery({
    queryKey: ["lookup-item-statuses"],
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/item-statuses`);
      return response.data;
    },
    staleTime: 60 * 60 * 1000,
  });

/**
 * Consumer-message types (legacy AdvancedSearch ddlConsumerMessage). Pulled
 * straight from CONSUMER_MESSAGE_TYPE. 5-minute cache.
 */
export const useConsumerMessageTypes = () =>
  useQuery({
    queryKey: ["lookup-consumer-message-types"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/consumer-message-types`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Coupon types (legacy AdvancedSearch ddlCoupon). Pulled from COUPON_TYPE.
 * 5-minute cache.
 */
export const useCouponTypes = () =>
  useQuery({
    queryKey: ["lookup-coupon-types"],
    queryFn: async (): Promise<NumericLookupRow[]> => {
      const response = await apiClient.get<NumericLookupResponse>(
        `${baseUrl}/coupon-types`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

/**
 * Deal-owners list (legacy AdvancedSearch ddlDealOwner). Distinct user ids
 * that own at least one DEAL, joined to RP_USER_PROFILE for the friendly
 * name. Used by the AdvancedSearch Deal Owner DDL.
 */
export interface DealOwnerListRow {
  userId: string;
  fullName: string;
}
interface DealOwnersListResponse {
  items: DealOwnerListRow[];
}
export const useDealOwnersList = () =>
  useQuery({
    queryKey: ["lookup-deal-owners-list"],
    queryFn: async (): Promise<DealOwnerListRow[]> => {
      const response = await apiClient.get<DealOwnersListResponse>(
        `${baseUrl}/deal-owners-list`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

// ---- BulkEditItems lookups (audit 26 — vendor scanback card) ----------
// Three small static lookups needed by the rebuilt BulkEditItems page:
// PS_BUS_UNIT_MV (BindScanbackBusinessUnit), PS_ITEM_ENTRY_MV
// (BindScanbackEntryTypes) and UNITS_OF_MEASURE (ddlItemProjUtsUOM). All
// share a { code, label } shape and are cached server- and client-side
// for 5 minutes.

export interface BusinessUnitRow {
  code: string;
  description: string;
  label: string;
}
interface BusinessUnitsResponse {
  items: BusinessUnitRow[];
}

export const useBusinessUnits = () =>
  useQuery({
    queryKey: ["lookup-business-units"],
    queryFn: async (): Promise<BusinessUnitRow[]> => {
      const response = await apiClient.get<BusinessUnitsResponse>(
        `${baseUrl}/business-units`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export interface EntryTypeRow {
  code: string;
  label: string;
}
interface EntryTypesResponse {
  items: EntryTypeRow[];
}

export const useEntryTypes = () =>
  useQuery({
    queryKey: ["lookup-entry-types"],
    queryFn: async (): Promise<EntryTypeRow[]> => {
      const response = await apiClient.get<EntryTypesResponse>(
        `${baseUrl}/entry-types`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

export interface UnitOfMeasureRow {
  code: string;
  description: string;
  label: string;
}
interface UnitsOfMeasureResponse {
  items: UnitOfMeasureRow[];
}

export const useUnitsOfMeasure = () =>
  useQuery({
    queryKey: ["lookup-units-of-measure"],
    queryFn: async (): Promise<UnitOfMeasureRow[]> => {
      const response = await apiClient.get<UnitsOfMeasureResponse>(
        `${baseUrl}/units-of-measure`
      );
      return response.data.items ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

// Movement code is a fixed two-value DropDownList in BulkEditItems
// (Advantage Card "A" and Total "T"). Legacy hard-codes these inline as
// ListItem entries; expose the same pair as a constant so the SPA can
// render the dropdown without an extra round-trip.
export const MOVEMENT_CODES: { code: string; label: string }[] = [
  { code: "A", label: "Advantage Card" },
  { code: "T", label: "Total" },
];

/**
 * Vendor typeahead (ddlVendor). Server-side LIKE match on VEND_NME so the
 * SPA AutoComplete can issue per-keystroke queries against the ~1900-row
 * vendor list without ever shipping the full domain to the browser.
 */
export const useVendorsLookup = (scope: CascadingScope, search?: string) =>
  useQuery({
    queryKey: ["lookup-vendors", scope.catMgrCode, scope.banner, search ?? ""],
    enabled: cascadingEnabled(scope),
    queryFn: async (): Promise<CodeLabelEnvelope> => {
      const response = await apiClient.get<CodeLabelEnvelope>(`${baseUrl}/vendors`, {
        params: {
          catMgrCode: scope.catMgrCode ?? undefined,
          banner: scope.banner ?? undefined,
          search: search || undefined,
          take: 50,
        },
      });
      return response.data;
    },
  });
